import {
  AlignSelf,
  Box,
  BoxProps,
  Display,
  DisplayPosition,
  GridDisplay,
  JustifyContent,
  ResponsiveValue,
} from '@gousto-internal/citrus-react';
import React from 'react';

export const gridCols = '1fr';
export const gridRows = '30px 170px auto';
export enum GridAreas {
  IMAGE = '1 / 1 / 3 / 1',
  IMAGE_TOP = 'image-top',
  IMAGE_BOTTOM = 'image-bottom',
  FOOTER = 'footer',
}
export const gridTemplateAreas = `'${GridAreas.IMAGE_TOP}' '${GridAreas.IMAGE_BOTTOM}' '${GridAreas.FOOTER}'`;
export const IMAGE_HEIGHTS: ResponsiveValue<string> = ['200px'];

type ContainerProps = { children: React.ReactNode } & BoxProps;
export function Container(props: ContainerProps) {
  const { children, ...rest } = props;
  return (
    <Box
      display={GridDisplay.Grid}
      gridTemplateRows={gridRows}
      gridTemplateColumns={gridCols}
      gridTemplateAreas={gridTemplateAreas}
      height="100%"
      position={DisplayPosition.Relative}
      {...rest}
    >
      {children}
    </Box>
  );
}

/**
 * Within RecipeCard, places item on a grid line
 * Must be used in conjunction with GridLineItem component
 */
export function PlaceItemOnGridLine(props: {
  children: React.ReactNode[] | React.ReactNode;
  gridArea: ResponsiveValue<GridAreas>;
}) {
  return (
    <Box
      gridArea={props.gridArea}
      height="0px"
      display={Display.Flex}
      justifyContent={JustifyContent.SpaceBetween}
      paddingH={4}
    >
      {props.children}
    </Box>
  );
}

function GridLineItemStack(props: {
  children: React.ReactNode[] | React.ReactNode;
  justifyContent: JustifyContent;
}) {
  const childrenElements = React.Children.toArray(props.children).filter(Boolean);

  return (
    <Box display={Display.Flex} justifyContent={props.justifyContent} gap={1}>
      {childrenElements.map((child, index) => (
        <GridLineItem key={index}>{child}</GridLineItem>
      ))}
    </Box>
  );
}

export function GridLineStackFromStart(props: { children: React.ReactNode[] | React.ReactNode }) {
  return GridLineItemStack({ children: props.children, justifyContent: JustifyContent.FlexStart });
}

export function GridLineStackFromEnd(props: { children: React.ReactNode[] | React.ReactNode }) {
  return GridLineItemStack({
    children: Array.isArray(props.children) ? [...props.children].reverse() : props.children,
    justifyContent: JustifyContent.FlexEnd,
  });
}

/**
 * Vertically aligns children to a RecipeCard grid line
 */
export function GridLineItem(props: { children: React.ReactNode }) {
  return (
    <Box
      display={Display.InlineBlock}
      style={{ transform: 'translate(0, -50%)' }}
      alignSelf={AlignSelf.Start}
    >
      {props.children}
    </Box>
  );
}
